import React, { FC, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { ProductsUI } from '../../ui/posts/types';
import { Typography } from '../../ui/typography/Typography';
import { isBloggerBusiness } from '../../utils/accessService';
import { getIsMobile } from '../../utils/getIsMobile';
import { useTranslation } from '../../utils/useTranslation';
import { BusinessUI } from '../business/types';
import { SmartDrawer } from '../feedback/SmartDrawer';
import { IconClose } from '../icon/IconClose';
import { IconProduct } from '../icon/IconProduct';
import { Button } from '../input/Button';
import { Flex } from '../layout/Flex';
import { PostBusinessInfo } from './PostBusinessInfo/PostBusinessInfo';
import { PostProductGrid } from './PostProductGrid/PostProductGrid';
import { PostProductsFixedContainer } from './PostProductsFixedContainer/PostProductsFixedContainer';
import { PostSingleProduct } from './PostSingleProduct/PostSingleProduct';

interface TrackProductClickOutProps {
    url?: string;
    target: string;
    productId?: string;
}

interface PostProductsDrawerProps {
    products: ProductsUI;
    business: BusinessUI;
    handleClose: VoidFunction;
    trackProductClickOut: (props: TrackProductClickOutProps) => void;
    onCatalogClick: VoidFunction;
}

export const PostProductsDrawer: FC<PostProductsDrawerProps> = ({
    handleClose,
    products,
    business,
    trackProductClickOut,
    onCatalogClick,
}) => {
    const [step, setStep] = useState<'OPEN' | 'CLOSE'>('OPEN');
    const t = useTranslation();
    const { isMobile } = getIsMobile();

    const onClose = useCallback(() => {
        setStep('CLOSE');
        handleClose();
    }, [handleClose]);

    const onProductClick = useCallback(
        (productId: string, url: string) => {
            trackProductClickOut({
                url,
                target: 'product',
                productId,
            });
        },
        [trackProductClickOut],
    );

    const onBusinessClick = useCallback(
        (url: string) => {
            trackProductClickOut({
                url,
                target: 'more_products',
            });
        },
        [trackProductClickOut],
    );

    return products.items.length > 1 ? (
        <SmartDrawer
            step={step}
            onClose={onClose}
            titleContent={
                <Typography variant="title3" weight="bold">
                    {t(products.title)}
                </Typography>
            }
            mainStyles={{ pb: 0, px: 0 }}
            mainContent={
                <>
                    <PostProductGrid products={products.items} onProductClick={onProductClick} />

                    {business.siteUrl && !isBloggerBusiness(business.type) ? (
                        <PostBusinessInfo
                            px={2}
                            business={business}
                            businessButtonTitle={products.businessButtonTitle}
                            onBusinessClick={onBusinessClick}
                        />
                    ) : null}

                    <PostProductsFixedContainer p={2}>
                        <Flex width="100%" spacing={1}>
                            {business.catalogUrl ? (
                                <Button
                                    size="l"
                                    as={Link}
                                    to={business.catalogUrl}
                                    width="100%"
                                    color="secondary"
                                    spacing={0.5}
                                    onClick={onCatalogClick}
                                >
                                    <IconProduct fontSize={20} />
                                    {t(business.catalogTitle)}
                                </Button>
                            ) : null}
                        </Flex>
                    </PostProductsFixedContainer>
                </>
            }
        />
    ) : (
        <SmartDrawer
            step={step}
            defaultCloseButtonEnabled={false}
            onClose={onClose}
            closeButtonSlot={
                isMobile ? undefined : (
                    <Flex
                        cursor="pointer"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius={2.5}
                        backgroundColor="var(--control-secondary-blur-background)"
                        position="absolute"
                        top={2}
                        right={2}
                        zIndex={10}
                        width="40px"
                        height="40px"
                    >
                        <IconClose size={24} />
                    </Flex>
                )
            }
            mainStyles={{ p: 0 }}
            mainContent={(_onClose) => (
                <>
                    <PostSingleProduct
                        product={products.items[0]}
                        business={business}
                        onBuyOnSiteClick={onProductClick}
                        onCatalogClick={onCatalogClick}
                        businessButtonTitle={products.businessButtonTitle}
                        onBusinessClick={onBusinessClick}
                    />
                </>
            )}
        />
    );
};
